/**
 * StageControls — touch controls under the playfield on mobile:
 * a d-pad (hold) on the left, JUMP on the right, and a W | C pair that is
 * dimmed until the woodchuck stands at a work spot.
 */

import { FONT_SIZE, fontStyle } from "../FontScale";

function button(scene, { x, y, w, h, label, color, depth = 900 }) {
  const g = scene.add.graphics().setDepth(depth);
  const draw = (alpha) => {
    g.clear();
    g.fillStyle(color, alpha);
    g.fillRoundedRect(x, y, w, h, 10);
    g.lineStyle(2, color, 0.8);
    g.strokeRoundedRect(x, y, w, h, 10);
  };
  draw(0.25);
  const text = scene.add
    .text(x + w / 2, y + h / 2, label, fontStyle(FONT_SIZE.HEADING, "#FFFFFF", { stroke: "#000000", strokeThickness: 3 }))
    .setOrigin(0.5)
    .setDepth(depth + 1);
  const zone = scene.add.zone(x + w / 2, y + h / 2, w, h).setInteractive().setDepth(depth + 2);
  return { g, text, zone, draw, parts: [g, text, zone] };
}

/**
 * @param {Phaser.Scene} scene
 * @param {{ top: number, height: number }} area
 * @param {(key: string) => void} send - receives L+/L-/R+/... and J/W/C
 */
export function createStageControls(scene, area, send) {
  scene.input.addPointer(3);
  const W = scene._W;
  const pad = 8;
  const size = Math.min(76, (area.height - pad * 3) / 2);
  const bottom = area.top + area.height - pad - size;
  const parts = [];

  const hold = (spec, key) => {
    const b = button(scene, spec);
    let down = false;
    const release = () => {
      if (!down) return;
      down = false;
      b.draw(0.25);
      send(`${key}-`);
    };
    b.zone.on("pointerdown", () => {
      down = true;
      b.draw(0.55);
      send(`${key}+`);
    });
    b.zone.on("pointerup", release);
    b.zone.on("pointerout", release);
    parts.push(...b.parts);
  };
  const tap = (spec, key) => {
    const b = button(scene, spec);
    b.zone.on("pointerdown", () => {
      b.draw(0.55);
      send(key);
    });
    b.zone.on("pointerup", () => b.draw(0.25));
    b.zone.on("pointerout", () => b.draw(0.25));
    parts.push(...b.parts);
    return b;
  };

  hold({ x: pad, y: bottom, w: size, h: size, label: "◀", color: 0x00ffff }, "L");
  hold({ x: pad * 2 + size, y: bottom, w: size, h: size, label: "▶", color: 0x00ffff }, "R");
  hold({ x: pad + size / 2 + pad / 2, y: bottom - size - pad, w: size, h: size, label: "▲", color: 0x00ffff }, "U");
  hold({ x: pad * 3 + size * 1.5, y: bottom - size - pad, w: size * 0.8, h: size, label: "▼", color: 0x00ffff }, "D");
  tap({ x: W - pad - size * 1.6, y: bottom - size * 0.3, w: size * 1.6, h: size * 1.3, label: "JUMP", color: 0xffd84a }, "J");

  // W | C chuck-wood pair: top centre, dimmed away from a work spot
  const wcW = Math.min(70, (W - (pad * 5 + size * 3.6)) / 2);
  const wcX = W / 2 - wcW - pad / 2 + size * 0.1;
  const w = tap({ x: wcX, y: area.top + pad, w: wcW, h: size, label: "W", color: 0x7bc74a, depth: 905 }, "W");
  const c = tap({ x: wcX + wcW + pad, y: area.top + pad, w: wcW, h: size, label: "C", color: 0xd4884a, depth: 905 }, "C");
  const wcParts = [...w.parts, ...c.parts];

  return {
    setWorkActive(active) {
      if (active === this.active) return;
      this.active = active;
      wcParts.forEach((p) => p.setAlpha(active ? 1 : 0.3));
    },
    destroy() {
      parts.splice(0).forEach((p) => p.destroy());
    },
  };
}
